import {
  CollisionDetection,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { DragHandleDots2Icon } from "@radix-ui/react-icons";
import { ReactNode, createContext, useContext, useState } from "react";

/** The list with the item at `from` moved to `to`. */
export function moved<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * The item under the pointer, else the one the dragged item overlaps. Let
 * go outside the list and nothing moves.
 */
const underPointer: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return hits.length > 0 ? hits : rectIntersection(args);
};

type Sorting = { ids: string[]; active: string | null; over: string | null };

const SortingContext = createContext<Sorting>({
  ids: [],
  active: null,
  over: null,
});

/**
 * A list reordered by dragging each item's grip, with the CRM board's drag
 * and drop (dnd-kit). Items only move within this list. While dragging, a
 * line shows where the item will land.
 */
export function SortableList({
  ids,
  onMove,
  overlay,
  children,
}: {
  ids: string[];
  onMove: (from: number, to: number) => void;
  /** What follows the pointer: the dragged item's name. */
  overlay: (id: string) => ReactNode;
  children: ReactNode;
}) {
  const [active, setActive] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const sensors = useSensors(
    // A press that moves less than 8px is a click, not a drag.
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    // On touch screens a long press picks the item up; a swipe still scrolls.
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  );

  const stop = () => {
    setActive(null);
    setOver(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={underPointer}
      onDragStart={(event) => setActive(String(event.active.id))}
      onDragOver={(event) => setOver(event.over ? String(event.over.id) : null)}
      onDragEnd={(event) => {
        stop();
        if (!event.over) return;
        const from = ids.indexOf(String(event.active.id));
        const to = ids.indexOf(String(event.over.id));
        if (from >= 0 && to >= 0 && from !== to) onMove(from, to);
      }}
      onDragCancel={stop}
    >
      <SortingContext.Provider value={{ ids, active, over }}>
        {children}
      </SortingContext.Provider>
      <DragOverlay zIndex={45} dropAnimation={null}>
        {active ? (
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-lg">
            <DragHandleDots2Icon className="text-gray-400" />
            {overlay(active)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * One item of a SortableList: `rowRef` goes on the item (where it can be
 * dropped), `grip` is its drag handle. `line` says where a dragged item
 * would land: above or below this one.
 */
export function useSortableItem(id: string) {
  const { ids, active, over } = useContext(SortingContext);
  const { setNodeRef: rowRef } = useDroppable({ id });
  const {
    setNodeRef: gripRef,
    listeners,
    attributes,
    isDragging,
  } = useDraggable({ id });
  const from = active === null ? -1 : ids.indexOf(active);
  const at = ids.indexOf(id);
  const line: "above" | "below" | null =
    active !== null && over === id && active !== id
      ? from < at
        ? "below"
        : "above"
      : null;
  return { rowRef, gripRef, listeners, attributes, isDragging, line };
}

/** The ⋮⋮ grip an item is dragged by. */
export function Grip({
  label,
  item,
}: {
  label: string;
  item: ReturnType<typeof useSortableItem>;
}) {
  return (
    <button
      type="button"
      ref={item.gripRef}
      {...item.listeners}
      {...item.attributes}
      className="cursor-grab touch-none rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 active:cursor-grabbing"
      aria-label={label}
      title="Drag to reorder"
    >
      <DragHandleDots2Icon />
    </button>
  );
}

/**
 * The drop line as a top or bottom inset border: on the item, or for an
 * item that is a table body, on the cells of its first or last row (rows
 * draw no shadow of their own).
 */
export function lineClass(
  line: "above" | "below" | null,
  tableBody = false
): string {
  if (line === "above") {
    return tableBody
      ? "[&>tr:first-child>td]:shadow-[inset_0_2px_0_0_#98acfc]"
      : "shadow-[inset_0_2px_0_0_#98acfc]";
  }
  if (line === "below") {
    return tableBody
      ? "[&>tr:last-child>td]:shadow-[inset_0_-2px_0_0_#98acfc]"
      : "shadow-[inset_0_-2px_0_0_#98acfc]";
  }
  return "";
}
